import React, { createContext, useContext, useEffect, useState } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("clariva_user") || "null"); }
    catch { return null; }
  });
  const [loading, setLoading] = useState(false);

  const persist = (token, u) => {
    if (token) localStorage.setItem("clariva_token", token);
    localStorage.setItem("clariva_user", JSON.stringify(u));
    setUser(u);
  };

  const setToken = (token) => {
    if (token) localStorage.setItem("clariva_token", token);
  };

  const refreshMe = async () => {
    try {
      const { data } = await api.get("/auth/me");
      persist(null, data);
      return data;
    } catch (e) { /* ignore */ return null; }
  };

  const login = async (email, password) => {
    setLoading(true);
    try {
      // Check whether this email's tenant requires LDAP auth
      let ldap = false;
      try {
        const hint = await api.get("/auth/sso-hint", { params: { email } });
        ldap = !!hint.data?.ldap;
      } catch (e) { /* fall through to standard login */ }
      const path = ldap ? "/auth/ldap-login" : "/auth/login";
      const { data } = await api.post(path, { email, password });
      persist(data.access_token, data.user);
      return data.user;
    } finally { setLoading(false); }
  };

  const register = async (payload) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", payload);
      persist(data.access_token, data.user);
      return data.user;
    } finally { setLoading(false); }
  };

  const googleLogin = async (session_id) => {
    setLoading(true);
    try {
      const { data } = await api.post("/auth/google", { session_id });
      persist(data.access_token, data.user);
      return data.user;
    } finally { setLoading(false); }
  };

  const endImpersonation = async () => {
    try {
      const { data } = await api.post("/auth/end-impersonation");
      persist(data.access_token, data.user);
      return data.user;
    } catch (e) { return null; }
  };

  const logout = () => {
    localStorage.removeItem("clariva_token");
    localStorage.removeItem("clariva_user");
    setUser(null);
  };

  useEffect(() => {
    if (!user) return;
    api.get("/auth/me")
      .then((r) => { persist(null, r.data); })
      .catch(() => logout());
  }, []); // eslint-disable-line

  return (
    <AuthCtx.Provider value={{ user, loading, login, register, googleLogin, logout, setToken, refreshMe, endImpersonation }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
